/**
 * @fileoverview AuthContext - Global authentication state provider
 * 
 * Provides user session and authentication state throughout the app.
 * Listens for auth state changes and invalidates React Query cache
 * on sign-in/sign-out events.
 * 
 * @module contexts/AuthContext
 */

import React, { createContext, useContext, useEffect, ReactNode } from "react";
import { User, Session } from "@supabase/supabase-js";
import { supabase } from "@/integrations/supabase/client";
import { useQueryClient } from "@tanstack/react-query";
import { queryKeys } from "@/lib/query-keys";
import { useAuthSession } from "@/hooks/useAuthSession";
import { useCookieConsentMigration } from "@/hooks/useCookieConsentMigration";

interface AuthContextType {
  user: User | null;
  session: Session | null;
  loading: boolean;
}

const AuthContext = createContext<AuthContextType>({
  user: null,
  session: null,
  loading: true,
});

export function AuthProvider({ children }: { children: ReactNode }) {
  const queryClient = useQueryClient();
  const { data: session, isLoading } = useAuthSession();
  
  // Migrate cookie consent from localStorage to database on login
  useCookieConsentMigration();

  useEffect(() => {
    // Only listen for auth events to invalidate cache
    const { data: { subscription } } = supabase.auth.onAuthStateChange(
      (event) => {
        // Only invalidate on actual sign-in/sign-out (not on token refresh)
        if (event === 'SIGNED_IN') {
          queryClient.invalidateQueries({ queryKey: queryKeys.auth.session() });
          queryClient.invalidateQueries({ queryKey: queryKeys.auth.user() });
        }
        
        if (event === 'SIGNED_OUT') {
          queryClient.clear();
        }
      }
    );

    return () => subscription.unsubscribe();
  }, [queryClient]);

  const value = React.useMemo(
    () => ({ 
      user: session?.user ?? null, 
      session: session ?? null, 
      loading: isLoading 
    }),
    [session, isLoading]
  );

  return (
    <AuthContext.Provider value={value}>
      {children}
    </AuthContext.Provider>
  );
}

export function useAuthContext() {
  const context = useContext(AuthContext);
  if (context === undefined) {
    throw new Error("useAuthContext must be used within an AuthProvider");
  }
  return context;
}
